import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LoyaltyBadge } from "@/components/app/LoyaltyBadge";
import { Crown, Medal, Trophy, ShieldStar, CheckCircle, Lock } from "@phosphor-icons/react";

const TIERS = [
  { key: "bronze", label: "Bronze", icon: Medal, perks: ["Suporte padrão", "Lembretes automáticos"] },
  { key: "silver", label: "Silver", icon: ShieldStar, perks: ["Desconto progressivo", "Suporte prioritário"] },
  { key: "gold",   label: "Gold",   icon: Trophy, perks: ["Desconto Gold", "Acesso a campanhas"] },
  { key: "king",   label: "King",   icon: Crown, perks: ["Maior desconto", "Brindes exclusivos", "Atendimento VIP"] },
];

const ClientBenefits = () => {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/client/me").then(({ data }) => setMe(data)).catch(() => {}); }, []);

  if (!me) return <div className="text-zinc-500">Carregando...</div>;
  const settings = me.settings || {};
  const thresholds = settings.loyalty_thresholds_months || {};
  const discounts = settings.loyalty_discounts || {};
  const order = ["bronze", "silver", "gold", "king"];
  const current = me.loyalty_tier;
  const idxCurrent = order.indexOf(current);
  const next = order[idxCurrent + 1];
  const monthsForNext = next ? thresholds[next] : null;
  const monthsLeft = next ? Math.max(0, (monthsForNext || 0) - me.months_active) : 0;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-semibold">Programa de fidelidade</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Seus benefícios</h1>
      </div>

      <Card className="border-zinc-200 rounded-2xl p-6 card-soft">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Nível atual</div>
            <div className="mt-2"><LoyaltyBadge tier={current} size="lg"/></div>
          </div>
          <div className="text-right">
            <div className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">Tempo como cliente</div>
            <div className="metric-num text-3xl mt-1">{me.months_active} <span className="text-base font-normal text-zinc-500">{me.months_active === 1 ? "mês" : "meses"}</span></div>
          </div>
        </div>
        {next && (
          <div className="mt-5">
            <div className="text-sm text-zinc-600">
              Faltam <strong className="text-zinc-900">{monthsLeft} {monthsLeft === 1 ? "mês" : "meses"}</strong> para alcançar <strong className="capitalize">{next}</strong>.
            </div>
            <div className="mt-2 h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.min(100, ((me.months_active / (monthsForNext || 1)) * 100))}%` }}/>
            </div>
          </div>
        )}
      </Card>

      <div className="grid sm:grid-cols-2 gap-3">
        {TIERS.map((t, i) => {
          const unlocked = i <= idxCurrent;
          const tierCls = { bronze: "tier-bronze", silver: "tier-silver", gold: "tier-gold", king: "tier-king" }[t.key];
          return (
            <Card key={t.key} className={`border-zinc-200 rounded-2xl p-5 card-soft transition-all ${unlocked ? "" : "opacity-60"}`} data-testid={`tier-${t.key}`}>
              <div className="flex items-center justify-between">
                <div className={`w-11 h-11 rounded-xl grid place-items-center ${tierCls}`}>
                  <t.icon size={20} weight="fill"/>
                </div>
                {unlocked ? <CheckCircle weight="fill" className="text-emerald-600" size={20}/> : <Lock className="text-zinc-400" size={18}/>}
              </div>
              <div className="font-display text-xl font-semibold mt-3">{t.label}</div>
              <div className="text-xs text-zinc-500 mt-0.5">A partir de {thresholds[t.key] || 0} {(thresholds[t.key] || 0) === 1 ? "mês" : "meses"} · {discounts[t.key] || 0}% desc.</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0"/>
                    {p}
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ClientBenefits;
